import axios from "axios"

import { jwtDecode } from "jwt-decode"
import { toast } from "react-toastify"
import {
  encryptedGETParams,
  isEmpty,
  isUserLoggedIn,
} from "../utils/commonFunction"
import { getAuthtoken } from "../utils/tokens"
import jwtDefaultConfig from "../utils/auth/jwt/jwtDefaultConfig"
import { store } from "../redux/store"
import { refreshToken, getPublicApiKey } from "./endpointUtils/auth"
import { setPublicKey } from "../redux/actions/userSessionAction"

const jwtDefault = { ...jwtDefaultConfig }

const getPublicAPIKeyFromStoreRef = () => {
  return store.getState().userSession.publicApiKey
}
const webp_support = () => {
  if (typeof document === "undefined") {
    return false
  }
  try {
    const elem = document.createElement("canvas")
    if (!!(!isEmpty(elem) && elem.getContext && elem.getContext("2d"))) {
      // was able or not to get WebP representation
      return elem.toDataURL("image/webp").indexOf("data:image/webp") === 0
    } else {
      // very old browser like IE 8, canvas not supported
      return false
    }
  } catch (error) {
    return false
  }
}

// Function to prepare headers
export const getPreparedHeaders = () => {
  const urlParts = typeof window !== "undefined" ? window.location.href.split("/").slice(4) : []
  const vertical = urlParts[0] || ""
  const project = urlParts[1] || ""
  const module_ = urlParts[2] || ""
  const token = localStorage?.getItem("token") || ""
  let username = ""
  let uniqueId = ""

  try {
    if (token) {
      const userDetails = jwtDecode(token)
      username = userDetails?.username
    }
    uniqueId = localStorage?.getItem("uniqueId") || ""
  } catch (error) {
    toast("Failed to decode token or retrieve user details:", {
      hideProgressBar: true,
      type: "error"
    })
  }

  return {
    vertical,
    project,
    module: module_,
    token,
    username,
    uniqueId
  }
}

export const prepareHeaders = (headers) => {
  const { vertical, project, module, token, username, uniqueId } = getPreparedHeaders()

  if (module) headers.set("module", module)
  if (vertical) headers.set("vertical", vertical)
  if (project) headers.set("project", project)
  if (username) headers.set("username", username)
  if (token) headers.set("authorization", `Bearer ${token}`)
  if (uniqueId) headers.set("Unique_id", uniqueId)

  return headers
}
let isAlreadyFetchingAccessToken = false
let isAlreadyFetchingPublicApiKey = false
let publicApiKeyPromise = null

const refreshPublicApiKey = async () => {
  if (isAlreadyFetchingPublicApiKey && publicApiKeyPromise) {
    return publicApiKeyPromise
  }

  isAlreadyFetchingPublicApiKey = true
  publicApiKeyPromise = getPublicApiKey()
    .then((res) => {
      if (res?.status === 200) {
        store.dispatch(setPublicKey(res.data))
      }
      return res
    })
    .finally(() => {
      isAlreadyFetchingPublicApiKey = false
      publicApiKeyPromise = null
    })

  return publicApiKeyPromise
}
// Endpoints that read `meter` straight from the query string, so it must not be encrypted
// into eData by encryptedGETParams
const PLAIN_METER_PARAM_ENDPOINTS = [
  "/api/hes/gas/tariff-info/",
  "prod-test-data/"
]

/**
 * Modules whose own screens deal with a 403 rather than letting the interceptor treat it as an
 * expired token. Their sub-module access is granted per user and only discovered when a request
 * comes back forbidden, so the module shows that as a message instead of reloading the page.
 *
 * Matched against the `module` header, which the request interceptor takes from the URL — the
 * same component rendered under a different module is therefore judged by where it is rendered.
 */
const FORBIDDEN_AWARE_MODULES = ["cmm"]

/**
 * And the same for requests that carry no module at all.
 *
 * A page outside any project — the SSL tracker — sends no module header to be matched on, so
 * its own endpoint is named here instead. Without this a user the backend refuses would have
 * their token refreshed and the page reloaded, only to be refused again: a reload loop that
 * never settles.
 */
const FORBIDDEN_AWARE_PATHS = ["/api/hes/gas/cert-expiry/"]

/** Broadcast when such a module is refused, so the module's shell can say so. */
export const FORBIDDEN_EVENT = "api:forbidden"

const isForbiddenAwareModule = (request) =>
  FORBIDDEN_AWARE_MODULES.includes(String(request?.headers?.module || "").toLowerCase())

const isForbiddenAwareRequest = (request) =>
  isForbiddenAwareModule(request) ||
  FORBIDDEN_AWARE_PATHS.some((path) => String(request?.url || "").includes(path))

/**
 * Announced rather than returned, because the screen that has to react is the module shell and
 * not the component whose request happened to be refused.
 */
const notifyForbidden = (request, response) => {
  if (typeof window === "undefined" || typeof window.CustomEvent !== "function") return

  window.dispatchEvent(
    new window.CustomEvent(FORBIDDEN_EVENT, {
      detail: {
        module: String(request?.headers?.module || "").toLowerCase(),
        url: request?.url || "",
        detail: response?.data?.data?.error?.detail || response?.data?.detail || ""
      }
    })
  )
}

// Axios instance configuration
const rawInstanceConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    // Add any default headers here if needed
  }
}

export const rawInstance = axios.create(rawInstanceConfig)

// Adding request interceptor to set the headers
rawInstance.interceptors.request.use(
  (config) => {
    const preparedHeaders = new Headers()
    prepareHeaders(preparedHeaders)
    preparedHeaders.forEach((value, key) => {
      config.headers[key] = value
    })

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const tokenInstanceConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    Accept: "application/json",
    "Content-Type": "application/json",
    WebpSupport: webp_support() ? "yes" : "no"
  }
}

export const tokenInstance = axios.create(tokenInstanceConfig)
tokenInstance.interceptors.request.use(async (tokenInstanceConfig) => {
  tokenInstanceConfig.headers.authtoken = getAuthtoken()
  const accessToken = localStorage.getItem("accessToken")
  let role
  const unique_id = localStorage.getItem("uniqueId")
  if (accessToken) {
    const roleDetails = jwtDecode(accessToken)
    // console.log(roleDetails);
    role = roleDetails?.userData?.role
  }
  // console.log(rdole);

  const url_split = decodeURI(window.location.href).replace("#/", "").split("/")
  // Pages that are not inside a project: their path has no vertical/project/module to read,
  // so sending the first segment as `vertical` would just be noise.
  const isProjectlessPage = ["admin", "ssl-tracker"].includes(url_split[3])
  if (isProjectlessPage && url_split.length < 6) {
    // Do Nothing
  } else {
    tokenInstanceConfig.headers.vertical = url_split[3]

    tokenInstanceConfig.headers.project = url_split[4]

    tokenInstanceConfig.headers.module = url_split[5]
  }
  if (unique_id) {
    tokenInstanceConfig.headers.unique_id = unique_id
  }
  if (role) {
    tokenInstanceConfig.headers.role = role
  }
  if (localStorage.getItem("accessToken")) {
    tokenInstanceConfig.headers.username = jwtDecode(
      localStorage.getItem("accessToken")
    ).userData.email
  }

  if (accessToken) {
    // ** eslint-disable-next-line no-param-reassign
    tokenInstanceConfig.headers.Authorization = `${jwtDefault.tokenType} ${accessToken}`
  }

  if (tokenInstanceConfig.method.toLowerCase() == "get") {
    const publicApiKey = getPublicAPIKeyFromStoreRef()
    if (publicApiKey && tokenInstanceConfig.params) {
      // ***note*** : these endpoints expect a plain `meter` query param, not the encrypted eData blob
      const requestUrl = tokenInstanceConfig.url || ""
      const excludeFromEncryption = PLAIN_METER_PARAM_ENDPOINTS.some((path) => requestUrl.includes(path))
        ? ["meter"]
        : []
      tokenInstanceConfig.params = await encryptedGETParams(publicApiKey, {
        ...tokenInstanceConfig.params
      }, excludeFromEncryption)
      // Mark request so we can selectively retry if key rotates and server returns 400.
      // Only when something was actually encrypted — otherwise a plain request's genuine 400
      // (e.g. a validation error) would pointlessly refresh the key and fire a second request.
      if (tokenInstanceConfig.params?.eData) {
        tokenInstanceConfig.headers["x-encrypted-params"] = "1"
      }
    }
  }

  return tokenInstanceConfig
})

tokenInstance.interceptors.response.use(
  function (response) {
    return response
  },
  async function (error) {
    const { config, response } = error

    const originalRequest = config
    const status = response?.status

    // If the app stays open for a long time, encrypted GET param requests can fail
    // when the server rotates the public key. Re-fetch key and retry once.
    if (
      status === 400 &&
      originalRequest &&
      !originalRequest._retryPublicApiKey &&
      String(originalRequest?.method || "").toLowerCase() === "get" &&
      originalRequest?.headers?.["x-encrypted-params"] === "1" &&
      originalRequest?.params
    ) {
      originalRequest._retryPublicApiKey = true
      try {
        const keyRes = await refreshPublicApiKey()
        if (keyRes?.status === 200) {
          return tokenInstance(originalRequest)
        }
      } catch (e) {
        console.log("error in refreshPublicApiKey", e);
      }
    }

    // A 403 from a module that handles permissions itself is an answer, not an expired token:
    // the backend is saying this user may not use that sub-module. Refreshing the token and
    // reloading would throw the page away and land the user back on the first tab, which is
    // what this avoids. The error is marked and announced, then rejected for the caller.
    if (status === 403 && isForbiddenAwareRequest(originalRequest)) {
      error.isPermissionDenied = true
      // Only a module has a shell listening for this; a projectless page handles its own.
      if (isForbiddenAwareModule(originalRequest)) notifyForbidden(originalRequest, response)
      return Promise.reject(error)
    }

    if ((status === 403 || status === 401)) {
      // Check if Refresh Token Validity is not expired and response status is 401 or 403
      if (
        localStorage.getItem("refreshToken") &&
        jwtDecode(localStorage.getItem("refreshToken")).exp > Date.now() / 1000
      ) {

        if ((status === 403 || status === 401) && isUserLoggedIn()) {
          const refreshTokenLocal = localStorage.getItem("refreshToken")
          localStorage.removeItem('accessToken')
          refreshToken({ refresh: refreshTokenLocal })
            .then((response) => {
              if (response.data.responseCode === 200) {
                localStorage.setItem("accessToken", response.data.data.result.access)
                window.location.reload(false);
              }
              if (response.data.responseCode === 401) {
                localStorage.clear()
                window.location.href = '/login'
                window.location.reload(true);
              }
            })
            .catch((error) => {
              localStorage.clear()
              window.location.href = '/login'
              window.location.reload(true);
            })

          const retryOriginalRequest = new Promise((resolve) => {
            this.addSubscriber((accessToken) => {
              originalRequest.headers.Authorization = `${jwtDefault.tokenType} ${accessToken}`
              resolve(axios(originalRequest))
            })
          })

          return retryOriginalRequest
        }
      } else {
        localStorage.clear()
        window.location.href = '/login'
        window.location.reload(true);
      }
    }

    return Promise.reject(error)
  }
)
